import { Button, Card, Group, SegmentedControl, Skeleton, Stack, Table, Text } from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { useMemo, useState } from "react";
import { formatBucketChartLabel } from "@mip-tg-bot-turntable/utils";
import { adminErrorMessage } from "../../../api.ts";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";
import { AnalyticsStatsFilters } from "./AnalyticsStatsFilters.tsx";
import {
  CHART_SERIES_TOP_N,
  CHAT_LINE_COLORS,
  reconcileSeriesKey,
  STATS_TABLE_PAGE_SIZE,
  type MemberReconcileSeries,
} from "./stats-shared.ts";
import { MEMBER_RECONCILE_SECTIONS, useAnalyticsStats } from "./useAnalyticsStats.ts";
import type { UntrackedJoinsNavFilters } from "./untracked-join-shared.ts";

type ReconcileMetric = "gap" | "ourJoined" | "memberDelta" | "memberCount";

type Props = {
  readonly botId: string;
  readonly onOpenUntrackedJoins?: (filters?: UntrackedJoinsNavFilters) => void;
};

type SummaryRow = {
  chatId: string;
  chatTitle: string;
  latestMemberCount: number;
  sumOurJoined: number;
  latestGap: number | null;
  latestDelta: number | null;
};

function latestGapAbs(s: MemberReconcileSeries): number {
  const last = s.points.at(-1);
  if (last?.gap === null || last?.gap === undefined) return -1;
  return Math.abs(last.gap);
}

export function MemberReconcileTab({ botId, onOpenUntrackedJoins }: Props) {
  const stats = useAnalyticsStats(botId, MEMBER_RECONCILE_SECTIONS);
  const { data, isLoading, isError, error, activeGranularity } = stats;
  const [reconcileMetric, setReconcileMetric] = useState<ReconcileMetric>("gap");
  const [detailChatId, setDetailChatId] = useState<string | null>(null);

  const list = data?.memberReconcile ?? [];

  const rows = useMemo((): SummaryRow[] => {
    return list
      .map((s) => {
        const last = s.points.at(-1);
        const sumOurJoined = s.points.reduce((acc, p) => acc + p.ourJoined, 0);
        return {
          chatId: s.chatId,
          chatTitle: s.chatTitle,
          latestMemberCount: last?.memberCount ?? 0,
          sumOurJoined,
          latestGap: last?.gap ?? null,
          latestDelta: last?.memberDelta ?? null,
        };
      })
      .sort((a, b) => {
        const ga = a.latestGap === null ? -1 : Math.abs(a.latestGap);
        const gb = b.latestGap === null ? -1 : Math.abs(b.latestGap);
        return gb - ga;
      });
  }, [list]);

  const topSeries = useMemo(() => {
    return [...list].sort((a, b) => latestGapAbs(b) - latestGapAbs(a)).slice(0, CHART_SERIES_TOP_N);
  }, [list]);

  const lineChart = useMemo(() => {
    if (topSeries.length === 0) {
      return {
        data: [] as Array<Record<string, string | number>>,
        series: [] as Array<{ name: string; label: string; color: string }>,
      };
    }
    const series = topSeries.map((s, i) => ({
      name: reconcileSeriesKey(s.chatId),
      label: s.chatTitle.length > 16 ? `${s.chatTitle.slice(0, 16)}...` : s.chatTitle,
      color: CHAT_LINE_COLORS[i % CHAT_LINE_COLORS.length] ?? "indigo.6",
    }));
    const bucketOrder: string[] = [];
    const bucketSeen = new Set<string>();
    for (const s of topSeries) {
      for (const p of s.points) {
        if (!bucketSeen.has(p.bucket)) {
          bucketSeen.add(p.bucket);
          bucketOrder.push(p.bucket);
        }
      }
    }
    const lineData = bucketOrder.map((bucket) => {
      const row: Record<string, string | number> = {
        bucket,
        bucketLabel: formatBucketChartLabel(bucket, activeGranularity),
      };
      for (const s of topSeries) {
        const pt = s.points.find((p) => p.bucket === bucket);
        let v = 0;
        if (pt !== undefined) {
          if (reconcileMetric === "gap") v = pt.gap ?? 0;
          else if (reconcileMetric === "ourJoined") v = pt.ourJoined;
          else if (reconcileMetric === "memberDelta") v = pt.memberDelta ?? 0;
          else v = pt.memberCount;
        }
        row[reconcileSeriesKey(s.chatId)] = v;
      }
      return row;
    });
    return { data: lineData, series };
  }, [topSeries, reconcileMetric, activeGranularity]);

  const detailSeries = useMemo(() => {
    if (detailChatId === null) return null;
    return list.find((s) => s.chatId === detailChatId) ?? null;
  }, [list, detailChatId]);

  const {
    page,
    setPage,
    pageSize,
    total,
    totalPages,
    pageItems: pagedRows,
  } = useClientPagination(
    rows,
    `${stats.range}|${stats.chatId ?? ""}|${stats.adId ?? ""}|${reconcileMetric}`,
    STATS_TABLE_PAGE_SIZE,
  );

  const metricLabel =
    reconcileMetric === "gap"
      ? "净增-归因差"
      : reconcileMetric === "ourJoined"
        ? "归因入群(事件)"
        : reconcileMetric === "memberDelta"
          ? "TG 净增"
          : "TG 人数";

  return (
    <Stack gap="md">
      {data !== undefined ? (
        <BotTimezoneNotice
          timezone={data.timezone}
          statsDay={data.statsDay}
          range={data.range}
          windowStartAt={data.windowStartAt}
          windowEndAt={data.windowEndAt}
        />
      ) : null}
      <AnalyticsStatsFilters stats={stats} showAdId={false} />

      {isLoading ? (
        <Stack gap="md">
          <Skeleton h={280} radius="md" />
          <Skeleton h={320} radius="md" />
        </Stack>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrorMessage(error)}
        </Text>
      ) : list.length === 0 ? (
        <Text c="dimmed" size="sm" py="md">
          每小时自动记 TG 群人数。至少等 1 小时才有首条快照。「业务今日 / 近 24
          小时(滚动)」为小时粒度。净增 ≠ 归因入群事件（含退群与未归因）。
        </Text>
      ) : (
        <>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs" wrap="wrap" align="flex-end">
              <Stack gap={2}>
                <Text fw={600} size="lg">
                  群组人数对账（TG 快照 vs 归因入群）
                </Text>
                <Text size="xs" c="dimmed">
                  折线只画 |缺口| Top {CHART_SERIES_TOP_N}（共 {list.length}{" "}
                  群）。下表分页全量；点行展开该群四线。缺口 &gt; 0 表示 TG 净增大于归因入群。
                </Text>
              </Stack>
              <SegmentedControl
                size="xs"
                value={reconcileMetric}
                onChange={(v) => setReconcileMetric(v as ReconcileMetric)}
                data={[
                  { value: "gap", label: "缺口" },
                  { value: "ourJoined", label: "归因入群" },
                  { value: "memberDelta", label: "TG净增" },
                  { value: "memberCount", label: "TG人数" },
                ]}
              />
            </Group>
            <Text size="sm" fw={500} mb="xs">
              分群对比 · {metricLabel}
            </Text>
            <LineChart
              h={280}
              data={lineChart.data}
              dataKey="bucketLabel"
              withTooltip
              withLegend
              legendProps={{ verticalAlign: "bottom", height: 48 }}
              tickLine="y"
              curveType="monotone"
              strokeWidth={2}
              dotProps={{ r: activeGranularity === "hour" ? 0 : 2 }}
              series={lineChart.series}
            />
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              摘要表（按 |缺口| 降序）
            </Text>
            <AdminTable minWidth={680}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>群组</Table.Th>
                  <Table.Th>最新人数</Table.Th>
                  <Table.Th>时段归因入群</Table.Th>
                  <Table.Th>最新净增</Table.Th>
                  <Table.Th>最新缺口</Table.Th>
                  <Table.Th style={{ width: 160 }}>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedRows.map((row) => {
                  const selected = detailChatId === row.chatId;
                  const gapColor =
                    row.latestGap === null
                      ? "dimmed"
                      : row.latestGap > 0
                        ? "orange"
                        : row.latestGap < 0
                          ? "teal"
                          : "dimmed";
                  return (
                    <Table.Tr
                      key={row.chatId}
                      style={{
                        cursor: "pointer",
                        background: selected ? "var(--mantine-color-blue-light)" : undefined,
                      }}
                      onClick={() => setDetailChatId(selected ? null : row.chatId)}
                    >
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="sm" fw={500} lineClamp={1}>
                            {row.chatTitle}
                          </Text>
                          <Text size="xs" c="dimmed" ff="monospace">
                            {row.chatId}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>{row.latestMemberCount.toLocaleString()}</Table.Td>
                      <Table.Td>{row.sumOurJoined.toLocaleString()}</Table.Td>
                      <Table.Td>
                        {row.latestDelta === null ? "-" : row.latestDelta.toLocaleString()}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={gapColor} fw={500}>
                          {row.latestGap === null ? "-" : row.latestGap.toLocaleString()}
                        </Text>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap={6} wrap="nowrap">
                          <Button
                            size="compact-xs"
                            variant={selected ? "filled" : "light"}
                            onClick={() => setDetailChatId(selected ? null : row.chatId)}
                          >
                            {selected ? "收起" : "详情"}
                          </Button>
                          {onOpenUntrackedJoins !== undefined &&
                          row.latestGap !== null &&
                          row.latestGap > 0 ? (
                            <Button
                              size="compact-xs"
                              variant="light"
                              color="orange"
                              onClick={() => onOpenUntrackedJoins({ chatId: row.chatId })}
                            >
                              未归因
                            </Button>
                          ) : null}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </AdminTable>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Card>

          {detailSeries !== null ? (
            <Card withBorder padding="md" radius="md" bg="gray.0">
              <Group justify="space-between" mb="xs" wrap="wrap">
                <Text fw={600} size="sm">
                  详情：{detailSeries.chatTitle}
                </Text>
                <Button size="compact-xs" variant="subtle" onClick={() => setDetailChatId(null)}>
                  关闭
                </Button>
              </Group>
              <LineChart
                h={260}
                data={detailSeries.points.map((p) => ({
                  bucketLabel: formatBucketChartLabel(p.bucket, activeGranularity),
                  memberCount: p.memberCount,
                  ourJoined: p.ourJoined,
                  memberDelta: p.memberDelta ?? 0,
                  gap: p.gap ?? 0,
                }))}
                dataKey="bucketLabel"
                withTooltip
                withLegend
                legendProps={{ verticalAlign: "bottom", height: 40 }}
                tickLine="y"
                curveType="monotone"
                strokeWidth={2}
                series={[
                  { name: "memberCount", label: "TG 人数", color: "indigo.6" },
                  { name: "ourJoined", label: "归因入群", color: "teal.6" },
                  { name: "memberDelta", label: "TG 净增", color: "orange.6" },
                  { name: "gap", label: "净增-归因差", color: "red.6" },
                ]}
              />
            </Card>
          ) : (
            <Text size="xs" c="dimmed">
              点表格行或「详情」展开单群四线细图（默认收起）。
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}
